import React, { useState, useEffect } from "react";
import { open_api_with_cookies } from "../../../Utils/network";
import FilterChip from "../../../components/Chip/FilterChip";
import LoadingOverlay from "../../../components/Loading/LoadingOverlay";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import "./AIDashboard.css";

const dayOptions = ["7 Days", "14 Days", "30 Days", "60 Days", "90 Days"];
const dayMap = {
  "7 Days": 7,
  "14 Days": 14,
  "30 Days": 30,
  "60 Days": 60,
  "90 Days": 90,
};

const formatDateTime = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AIDashboard = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("30 Days");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const days = dayMap[timeframe];
        const res = await open_api_with_cookies.get(
          `/myburgoai/admin/dashboard/?days=${days}`
        );
        setData(res.data);
      } catch (error) {
        console.error("Error fetching AI dashboard:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [timeframe]);

  if (isLoading) return <LoadingOverlay />;
  if (!data) return <div className="ai-dashboard">Failed to load data.</div>;

  const { overview, model_distribution, safety_breakdown, feedback_summary, daily_usage, top_users, conversation_phases } = data;

  // Format daily usage for chart
  const chartData = (daily_usage || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    }),
    messages: d.messages,
    tokens: d.tokens,
    sessions: d.sessions,
    users: d.users,
  }));

  return (
    <div className="ai-dashboard">
      <div className="ai-dashboard-header">
        <h2>AI Analytics Dashboard</h2>
        <div style={{ width: 160 }}>
          <FilterChip
            options={dayOptions}
            selected={timeframe}
            onSelect={setTimeframe}
          />
        </div>
      </div>

      {/* Metric Cards */}
      <div className="ai-metrics-grid">
        <div className="ai-metric-card highlight">
          <div className="metric-label">Total Users</div>
          <div className="metric-value">
            {overview.total_users.toLocaleString()}
          </div>
          <div className="metric-sub">
            {overview.onboarded_users} onboarded
          </div>
        </div>
        <div className="ai-metric-card">
          <div className="metric-label">Total Sessions</div>
          <div className="metric-value">
            {overview.total_sessions.toLocaleString()}
          </div>
          <div className="metric-sub">
            {overview.active_sessions} active
          </div>
        </div>
        <div className="ai-metric-card">
          <div className="metric-label">Total Messages</div>
          <div className="metric-value">
            {overview.total_messages.toLocaleString()}
          </div>
          <div className="metric-sub">
            {overview.user_messages} user / {overview.ai_messages} AI
          </div>
        </div>
        <div className="ai-metric-card">
          <div className="metric-label">Total Tokens</div>
          <div className="metric-value">
            {overview.total_tokens.toLocaleString()}
          </div>
          <div className="metric-sub">
            Est. cost: ${overview.total_cost.toFixed(2)}
          </div>
        </div>
        <div className="ai-metric-card">
          <div className="metric-label">Avg Messages/Session</div>
          <div className="metric-value">{overview.avg_messages_per_session}</div>
          <div className="metric-sub">across all sessions</div>
        </div>
        <div className="ai-metric-card">
          <div className="metric-label">Avg Response Time</div>
          <div className="metric-value">
            {overview.avg_response_time_ms > 1000
              ? `${(overview.avg_response_time_ms / 1000).toFixed(1)}s`
              : `${Math.round(overview.avg_response_time_ms)}ms`}
          </div>
          <div className="metric-sub">AI response latency</div>
        </div>
        <div className="ai-metric-card">
          <div className="metric-label">Total Images</div>
          <div className="metric-value">
            {overview.total_images.toLocaleString()}
          </div>
          <div className="metric-sub">uploaded + generated</div>
        </div>
        <div className="ai-metric-card">
          <div className="metric-label">Feedback Rate</div>
          <div className="metric-value">
            {feedback_summary.total_with_feedback > 0
              ? `${Math.round(
                  (feedback_summary.thumbs_up /
                    feedback_summary.total_with_feedback) *
                    100
                )}%`
              : "N/A"}
          </div>
          <div className="metric-sub">
            {feedback_summary.thumbs_up} up / {feedback_summary.thumbs_down} down
          </div>
        </div>
      </div>

      {/* Daily Messages Chart + Model Distribution */}
      <div className="ai-charts-row">
        <div className="ai-chart-card">
          <h3>Daily Messages</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis tick={{ fill: "#666" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255,255,255,0.95)",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              />
              <Bar dataKey="messages" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill="#8560fc" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="ai-chart-card">
          <h3>Model Distribution</h3>
          {(model_distribution || []).map((m, i) => {
            const total = model_distribution.reduce((a, b) => a + b.count, 0);
            const pct = total > 0 ? ((m.count / total) * 100).toFixed(1) : 0;
            return (
              <div key={i} className="breakdown-item">
                <span className="breakdown-label">
                  <span
                    className="breakdown-dot"
                    style={{
                      background: i === 0 ? "#7045fb" : i === 1 ? "#9d86fe" : "#c4b5fd",
                    }}
                  />
                  {m.ai_model}
                </span>
                <span className="breakdown-value">
                  {m.count.toLocaleString()} ({pct}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Breakdown Cards */}
      <div className="ai-breakdown-grid">
        {/* Safety Breakdown */}
        <div className="ai-breakdown-card">
          <h3>Safety Levels</h3>
          {Object.entries(safety_breakdown || {}).map(([level, count]) => (
            <div key={level} className="breakdown-item">
              <span className="breakdown-label">
                <span className={`breakdown-dot dot-${level}`} />
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </span>
              <span className="breakdown-value">{count.toLocaleString()}</span>
            </div>
          ))}
        </div>

        {/* Feedback */}
        <div className="ai-breakdown-card">
          <h3>Feedback Summary</h3>
          <div className="breakdown-item">
            <span className="breakdown-label">
              <span className="breakdown-dot dot-thumbs_up" />
              Thumbs Up
            </span>
            <span className="breakdown-value">
              {feedback_summary.thumbs_up.toLocaleString()}
            </span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">
              <span className="breakdown-dot dot-thumbs_down" />
              Thumbs Down
            </span>
            <span className="breakdown-value">
              {feedback_summary.thumbs_down.toLocaleString()}
            </span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">
              <span className="breakdown-dot dot-default" />
              Total Rated
            </span>
            <span className="breakdown-value">
              {feedback_summary.total_with_feedback.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Conversation Phases */}
        <div className="ai-breakdown-card">
          <h3>Conversation Phases</h3>
          {Object.entries(conversation_phases || {}).map(([phase, count]) => (
            <div key={phase} className="breakdown-item">
              <span className="breakdown-label">
                <span className="breakdown-dot dot-default" />
                {phase.charAt(0).toUpperCase() + phase.slice(1)}
              </span>
              <span className="breakdown-value">{count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Users */}
      <div className="ai-top-users">
        <h3>Top Users by Messages</h3>
        <div className="top-users-table">
          <div className="top-users-header">
            <div>#</div>
            <div>User</div>
            <div>Messages</div>
            <div>Tokens</div>
            <div>Sessions</div>
            <div>Last App Activity</div>
          </div>
          {(top_users || []).map((user, i) => (
            <div key={user.user_id} className="top-users-row">
              <div>
                <span className="rank-badge">{i + 1}</span>
              </div>
              <div className="top-user-identity">
                <span>{user.name || "Anonymous"}</span>
                {user.email && <small>{user.email}</small>}
                {user.phone && (
                  <small>
                    {[user.isd_code, user.phone].filter(Boolean).join(" ")}
                  </small>
                )}
              </div>
              <div>{(user.total_messages || 0).toLocaleString()}</div>
              <div>{(user.total_tokens || 0).toLocaleString()}</div>
              <div>{user.session_count}</div>
              <div className="top-user-activity">
                <small className={user.app_installed ? "app-installed" : ""}>
                  {user.app_installed ? "App installed" : "Installation not observed"}
                </small>
                <span>{formatDateTime(user.last_app_used_at)}</span>
                {user.last_app_opened_at && (
                  <small>Opened {formatDateTime(user.last_app_opened_at)}</small>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIDashboard;
